import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { searchRewards, createFeaturedReward } from "@/lib/api/admin";
import type { RewardSearchResult } from "@/lib/api/types";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Spinner";

type AddFeaturedRewardDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function requirementText(r: RewardSearchResult): string {
  return r.reward_type === "point"
    ? `${(r.points_required ?? 0).toLocaleString()} pts`
    : `${r.stamps_required ?? 0} stamps`;
}

function rewardKey(r: RewardSearchResult): string {
  return `${r.reward_type}-${r.reward_id}`;
}

export function AddFeaturedRewardDialog({ open, onOpenChange }: AddFeaturedRewardDialogProps) {
  const user = useAdminAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [results, setResults] = useState<RewardSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedItems, setSelectedItems] = useState<RewardSearchResult[]>([]);
  const [formError, setFormError] = useState("");

  async function handleSearch() {
    if (!user || !search.trim()) return;
    setIsSearching(true);
    try {
      const res = await searchRewards(user, search.trim(), 10);
      setResults(res);
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  }

  function addSelected(r: RewardSearchResult) {
    setSelectedItems((prev) =>
      prev.some((x) => rewardKey(x) === rewardKey(r)) ? prev : [...prev, r]
    );
  }

  function removeSelected(r: RewardSearchResult) {
    setSelectedItems((prev) => prev.filter((x) => rewardKey(x) !== rewardKey(r)));
  }

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const items = selectedItems;
      const outcomes = await Promise.allSettled(
        items.map((r) => createFeaturedReward(user!, r.reward_type, r.reward_id))
      );
      return { items, outcomes };
    },
    onSuccess: ({ items, outcomes }) => {
      void queryClient.invalidateQueries({ queryKey: ["featured-rewards"] });

      const succeededKeys = new Set<string>();
      const failed: { item: RewardSearchResult; message: string }[] = [];
      outcomes.forEach((outcome, i) => {
        const item = items[i];
        if (outcome.status === "fulfilled") {
          succeededKeys.add(rewardKey(item));
        } else {
          const message =
            outcome.reason instanceof Error ? outcome.reason.message : String(outcome.reason);
          failed.push({ item, message });
        }
      });

      const succeededCount = items.length - failed.length;
      if (succeededCount > 0) {
        toast.success(`${succeededCount} reward${succeededCount === 1 ? "" : "s"} added`);
      }

      if (failed.length > 0) {
        toast.error(
          failed.length === 1
            ? `"${failed[0].item.name}" failed to add: ${failed[0].message}`
            : `${failed.length} rewards failed to add`
        );
        // Keep only the failed ones selected so the admin can retry or remove them.
        setSelectedItems((prev) => prev.filter((r) => !succeededKeys.has(rewardKey(r))));
      } else {
        onOpenChange(false);
      }
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  function handleOpenChange(value: boolean) {
    if (!value) {
      setSearch("");
      setResults([]);
      setHasSearched(false);
      setSelectedItems([]);
      setFormError("");
    }
    onOpenChange(value);
  }

  function handleSubmit() {
    setFormError("");
    mutate();
  }

  const addLabel =
    selectedItems.length === 0
      ? "Add Rewards"
      : `Add ${selectedItems.length} Reward${selectedItems.length === 1 ? "" : "s"}`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md min-w-0">
        <DialogHeader>
          <DialogTitle>Add Featured Rewards</DialogTitle>
        </DialogHeader>

        <div className="min-w-0 space-y-4">
          <div className="space-y-1.5">
            <Label>Search rewards</Label>
            <div className="flex gap-2">
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Reward or store name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleSearch();
                }}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => void handleSearch()}
                disabled={isSearching || !search.trim()}
              >
                {isSearching ? <Spinner className="size-4 border-primary" /> : "Search"}
              </Button>
            </div>
          </div>

          {hasSearched && !isSearching && (
            <div className="space-y-1.5">
              <Label>Search Results</Label>
              {results.length === 0 ? (
                <p className="text-xs text-muted-foreground">No rewards found.</p>
              ) : (
                <ul className="divide-y rounded-md border text-sm max-h-72 overflow-y-auto">
                  {results.map((r) => {
                    const added = selectedItems.some((x) => rewardKey(x) === rewardKey(r));
                    return (
                      <li
                        key={rewardKey(r)}
                        className="flex items-center justify-between gap-2 px-3 py-2"
                      >
                        <div className="min-w-0 flex-1 space-y-0.5">
                          <span className="block truncate font-medium">{r.name}</span>
                          <p className="text-xs text-muted-foreground">{r.store_name}</p>
                          <p className="text-xs text-muted-foreground">{requirementText(r)}</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={added}
                          onClick={() => addSelected(r)}
                        >
                          {added ? "Added" : "Add"}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Selected ({selectedItems.length})</Label>
            {selectedItems.length === 0 ? (
              <p className="text-xs text-muted-foreground">No rewards selected yet.</p>
            ) : (
              <ul className="divide-y rounded-md border text-sm max-h-48 overflow-y-auto">
                {selectedItems.map((r) => (
                  <li
                    key={rewardKey(r)}
                    className="flex items-center justify-between gap-2 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <span className="block truncate font-medium">{r.name}</span>
                      <p className="text-xs text-muted-foreground">{r.store_name}</p>
                      <p className="text-xs text-muted-foreground">{requirementText(r)}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-7 shrink-0"
                      onClick={() => removeSelected(r)}
                    >
                      <X className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {formError && <p className="text-sm text-destructive">{formError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={selectedItems.length === 0 || isPending}>
            {isPending ? <Spinner className="size-4 border-white" /> : addLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
